import { PDFDocument, StandardFonts, rgb } from "pdf-lib";

const SAFE_RE = /^(https?:|data:(image\/|application\/pdf))/i;
export const isSafeDoc = (url) => SAFE_RE.test((url || "").trim());
export const isPdfDoc = (url) =>
  /^data:application\/pdf/i.test(url || "") || /\.pdf(\?|$)/i.test(url || "");
export const isImageDoc = (url) =>
  /^data:image\//i.test(url || "") || /\.(png|jpe?g|webp|gif)$/i.test(url || "");

// Fetch the raw bytes for a document (base64 data URL or remote http URL).
async function fetchBytes(url) {
  if (url.startsWith("data:")) {
    const base64 = url.split(",")[1] || "";
    const bin = atob(base64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
    return bytes;
  }
  const res = await fetch(url);
  const buf = await res.arrayBuffer();
  return new Uint8Array(buf);
}

const extFromUrl = (url, fallback) => {
  if (url.startsWith("data:")) {
    const m = /data:([^;]+)/.exec(url);
    if (m) return m[1].split("/")[1] || fallback;
  }
  const m = /\.([a-z0-9]+)(\?|$)/i.exec(url);
  return m ? m[1].toLowerCase() : fallback;
};

// Trigger a browser download for a data/remote URL.
export const downloadDoc = (url, filename) => {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || `document.${extFromUrl(url, "bin")}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
};

// Open the document in a new tab for printing (safe DOM, no document.write).
export const printDoc = (url) => {
  if (!isSafeDoc(url)) return;
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.body.style.margin = "0";
  if (isPdfDoc(url)) {
    const frame = w.document.createElement("iframe");
    frame.src = url;
    frame.style.cssText = "border:0;width:100%;height:100vh";
    frame.onload = () => setTimeout(() => { try { frame.contentWindow.print(); } catch (e) { /* cross-origin */ } }, 500);
    w.document.body.appendChild(frame);
  } else {
    const img = w.document.createElement("img");
    img.src = url;
    img.style.maxWidth = "100%";
    img.onload = () => setTimeout(() => { w.focus(); w.print(); }, 300);
    w.document.body.appendChild(img);
  }
};

// Merge all provided documents (images + PDFs) into a single downloadable PDF.
export async function buildCombinedPdf(docs, title) {
  const out = await PDFDocument.create();
  const font = await out.embedFont(StandardFonts.HelveticaBold);
  const A4 = [595.28, 841.89];

  for (const { url, label } of docs) {
    if (!url || !isSafeDoc(url)) continue;
    try {
      const bytes = await fetchBytes(url);
      if (isPdfDoc(url)) {
        const src = await PDFDocument.load(bytes, { ignoreEncryption: true });
        const pages = await out.copyPages(src, src.getPageIndices());
        pages.forEach((p) => out.addPage(p));
      } else {
        const ext = extFromUrl(url, "png");
        let img;
        if (ext === "jpg" || ext === "jpeg") img = await out.embedJpg(bytes);
        else {
          try { img = await out.embedPng(bytes); }
          catch { img = await out.embedJpg(bytes); }
        }
        const page = out.addPage(A4);
        const margin = 40;
        const maxW = A4[0] - margin * 2;
        const maxH = A4[1] - margin * 2 - 30;
        const scale = Math.min(maxW / img.width, maxH / img.height, 1);
        const w = img.width * scale;
        const h = img.height * scale;
        page.drawText(label || "Document", { x: margin, y: A4[1] - margin, size: 12, font, color: rgb(0.1, 0.1, 0.1) });
        page.drawImage(img, { x: margin, y: A4[1] - margin - 20 - h, width: w, height: h });
      }
    } catch (e) {
      // Unsupported image format (e.g. webp/gif) — add a placeholder page.
      const page = out.addPage(A4);
      page.drawText(`${label || "Document"} — could not embed (unsupported format)`, {
        x: 40, y: A4[1] - 60, size: 12, font, color: rgb(0.7, 0.1, 0.1),
      });
    }
  }

  if (out.getPageCount() === 0) {
    const page = out.addPage(A4);
    page.drawText("No documents provided.", { x: 40, y: A4[1] - 60, size: 14, font });
  }

  const pdfBytes = await out.save();
  const blob = new Blob([pdfBytes], { type: "application/pdf" });
  const blobUrl = URL.createObjectURL(blob);
  downloadDoc(blobUrl, `${(title || "seller").replace(/[^a-z0-9]+/gi, "_")}_documents.pdf`);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
}
